/**
 * Rótulos e cores do módulo de assinatura eletrônica.
 *
 * Centralizados para que a lista, o wizard e o painel do paciente falem a mesma
 * língua — status de envelope aparece em três telas diferentes.
 */

import type {
  CidConsentMode,
  CidConsentStatus,
  ContractTemplateCategory,
  DeliveryMethod,
  EnvelopeStatus,
  MedicalRecordContentType,
  MedicalRecordDeliveryFormat,
  MedicalRecordDeliveryStatus,
  MedicalRecordRecipientType,
  SignatureMethod,
  SignaturePurpose,
  SignerRole,
  SignerStatus,
} from '../types/signature';

type ChipTone = 'default' | 'info' | 'success' | 'warning' | 'error';

export const ENVELOPE_STATUS_LABELS: Record<EnvelopeStatus, string> = {
  draft: 'Rascunho',
  sent: 'Aguardando assinatura',
  in_progress: 'Parcialmente assinado',
  completed: 'Assinado',
  refused: 'Assinatura negada',
  cancelled: 'Cancelado',
  expired: 'Expirado',
};

export const ENVELOPE_STATUS_TONES: Record<EnvelopeStatus, ChipTone> = {
  draft: 'default',
  sent: 'warning',
  in_progress: 'info',
  completed: 'success',
  refused: 'error',
  cancelled: 'default',
  expired: 'default',
};

export const SIGNER_STATUS_LABELS: Record<SignerStatus, string> = {
  pending: 'Não abriu',
  viewed: 'Abriu o link',
  signed: 'Assinou',
  refused: 'Negou',
};

export const SIGNER_STATUS_TONES: Record<SignerStatus, ChipTone> = {
  pending: 'default',
  viewed: 'info',
  signed: 'success',
  refused: 'error',
};

export const SIGNER_ROLE_LABELS: Record<SignerRole, string> = {
  patient: 'Paciente',
  responsible: 'Responsável legal',
  professional: 'Profissional',
  witness: 'Testemunha',
  clinic: 'Clínica',
  recipient: 'Recebedor(a)',
};

export const SIGNATURE_PURPOSE_LABELS: Record<SignaturePurpose, string> = {
  medical_record_delivery: 'Entrega de prontuário',
  certificate_cid_consent: 'Autorização do CID',
};

export const MEDICAL_RECORD_RECIPIENT_LABELS: Record<MedicalRecordRecipientType, string> = {
  patient: 'O próprio paciente',
  legal_guardian: 'Responsável legal',
  attorney: 'Procurador(a)',
  authorized_third_party: 'Terceiro autorizado',
};

export const MEDICAL_RECORD_FORMAT_LABELS: Record<MedicalRecordDeliveryFormat, string> = {
  printed: 'Cópia impressa',
  digital: 'Cópia digital',
  both: 'Impressa e digital',
};

export const MEDICAL_RECORD_CONTENT_LABELS: Record<MedicalRecordContentType, string> = {
  evolutions: 'Evoluções clínicas',
  prescriptions: 'Receituários',
  certificates: 'Atestados e declarações',
  exam_orders: 'Pedidos de exame',
  exam_results: 'Resultados de exames',
  medical_reports: 'Laudos e relatórios',
  images: 'Imagens e radiografias',
  consent_terms: 'Termos de consentimento',
  other: 'Outros documentos',
};

export const MEDICAL_RECORD_DELIVERY_STATUS_LABELS: Record<MedicalRecordDeliveryStatus, string> = {
  pending_signature: 'Aguardando assinatura',
  signed: 'Recibo assinado',
  refused: 'Recibo negado',
  cancelled: 'Cancelado',
};

export const MEDICAL_RECORD_DELIVERY_STATUS_TONES: Record<MedicalRecordDeliveryStatus, ChipTone> = {
  pending_signature: 'warning',
  signed: 'success',
  refused: 'error',
  cancelled: 'default',
};

export const CID_CONSENT_STATUS_LABELS: Record<CidConsentStatus, string> = {
  not_requested: 'CID sem autorização',
  pending: 'Autorização pendente',
  signed: 'CID autorizado',
  refused: 'CID não autorizado',
  cancelled: 'Pedido cancelado',
};

export const CID_CONSENT_STATUS_TONES: Record<CidConsentStatus, ChipTone> = {
  not_requested: 'warning',
  pending: 'info',
  signed: 'success',
  refused: 'error',
  cancelled: 'default',
};

export const CID_CONSENT_MODE_LABELS: Record<CidConsentMode, string> = {
  off: 'Não pedir autorização',
  warn: 'Avisar ao imprimir sem autorização',
  required: 'Exigir autorização assinada',
};

export const SIGNATURE_METHOD_LABELS: Record<SignatureMethod, string> = {
  handwritten: 'Assinatura manuscrita',
  typed: 'Assinatura digitada',
  icp: 'Certificado digital',
};

export const SIGNATURE_METHOD_HINTS: Record<SignatureMethod, string> = {
  handwritten: 'O signatário desenha com o dedo ou caneta digital',
  typed: 'O signatário digita o nome completo',
  icp: 'ICP-Brasil A1 — exige um único signatário',
};

export const DELIVERY_METHOD_LABELS: Record<DeliveryMethod, string> = {
  device: 'Assinar no dispositivo',
  whatsapp: 'WhatsApp',
};

export const DELIVERY_METHOD_HINTS: Record<DeliveryMethod, string> = {
  device: 'Abre o QR code para o paciente assinar no celular dele, aqui na clínica',
  whatsapp: 'Envia o link pela API oficial para o WhatsApp do signatário',
};

export const CONTRACT_CATEGORY_LABELS: Record<ContractTemplateCategory, string> = {
  contract: 'Contrato de prestação de serviço',
  consent: 'Termo de consentimento',
  term: 'Termo / declaração',
  authorization: 'Autorização',
  other: 'Outro',
};

/** Leitura humana da trilha de auditoria. */
export const SIGNATURE_EVENT_LABELS: Record<string, string> = {
  seal_failed: 'Falha ao gerar o PDF final',
  created: 'Envelope criado',
  sent: 'Enviado para assinatura',
  link_opened: 'Link aberto',
  access_code_sent: 'Código de acesso enviado',
  access_code_failed: 'Código de acesso incorreto',
  identity_confirmed: 'Identidade confirmada',
  document_viewed: 'Documento visualizado',
  signed: 'Documento assinado',
  refused: 'Assinatura negada',
  completed: 'Processo concluído',
  cancelled: 'Envelope cancelado',
  downloaded: 'Documento baixado',
};

/** Envelope sem assinatura ainda pode ser cancelado; concluído, não. */
export function canCancelEnvelope(status: EnvelopeStatus): boolean {
  return status !== 'completed' && status !== 'cancelled';
}

export function isEnvelopeOpen(status: EnvelopeStatus): boolean {
  return status === 'sent' || status === 'in_progress';
}
